import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {MatDatepicker} from '@angular/material/datepicker';
import {MatSelectChange} from '@angular/material/select';
import {
    FilterNodeObj,
    FilterOperator,
    GroupNodeObj,
    KeyValueObj,
    MxNotificationService,
    MxUtilsService,
    OperatorDetailsObj,
    RuleNodeObj,
    RuleReaderInfoObj,
    TrackerVariableObj,
    VariableType
} from 'mx-core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BoFiltersService} from '../../services/bo-filters.service';
import {combineLatest, Observable, Subject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import moment, {Moment} from 'moment-timezone';

@Component({
    selector: 'bo-filter',
    templateUrl: './bo-filter.component.html',
    standalone: false,
    styleUrls: ['bo-filter.component.scss']
})


export class BoFilterComponent {

    @Input()
    public readonly: boolean;
    @Input()
    private parentForm: FormGroup;
    @Input()
    filterField: string;
    @Input()
    filterComparisonField: any;
    @Input()
    private variablesObservable: Observable<Array<TrackerVariableObj>>;
    @Output()
    initialized = new EventEmitter<boolean>();

    private operatorDetails: Array<OperatorDetailsObj>;
    private operatorDetailsMap: Map<FilterOperator, OperatorDetailsObj>;
    private dynamicOperandMap: Map<string, Array<KeyValueObj>>;
    public variables: Array<TrackerVariableObj>;
    public previousOperatorValue: string;

    public filterForm: FormGroup = new FormGroup({});
    public isFormInitialized: boolean = false;
    private _destroy$$: Subject<null> = new Subject<null>();

    constructor (private boFiltersService: BoFiltersService,
                 private formBuilder: FormBuilder,
                 private cdRef: ChangeDetectorRef,
                 private notificationService: MxNotificationService,
                 private utils: MxUtilsService) {
        this.operatorDetails = new Array<OperatorDetailsObj>();
        this.operatorDetailsMap = new Map<FilterOperator, OperatorDetailsObj>();
        this.dynamicOperandMap = new Map<string, Array<KeyValueObj>>();
    }

    ngOnInit(): void {
        if (!this.parentForm || !this.parentForm.controls) {
            return;
        }

        if (this.filterComparisonField) {
            this.parentForm.setControl('filterData', new FormControl(this.filterComparisonField));
            this.filterField = 'filterData';
        }

        let initFilter = this.parentForm.get([this.filterField]).value;
        combineLatest(
            this.boFiltersService.getOperators(),
            this.boFiltersService.getDynamicOperands(),
            this.variablesObservable,
        ).pipe(first())
            .subscribe(([allOperators, dynamicOperands, variables])  => {
                this.variables = variables;
                this.operatorDetails = allOperators;
                this.dynamicOperandMap = dynamicOperands;
                for (let opDetail of this.operatorDetails) {
                    this.operatorDetailsMap.set(opDetail.key, opDetail);
                }

                this.buildFilterControls(initFilter);
                this.isFormInitialized = true;
                this.parentForm.markAsPristine();
                this.cdRef.detectChanges();
                if (this.filterComparisonField) {
                    this.initialized.emit(true);
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }


    private buildFilterControls = (filter: FilterNodeObj) => {
        this._destroy$$.next(null);
        this.filterForm = this.buildForm(filter);
        this.parentForm.get([this.filterField]).setValidators(this.validateFilters);
        this.parentForm.get([this.filterField]).patchValue(this.filterForm.getRawValue(), {emitEvent: false});
        this.parentForm.get([this.filterField]).markAsDirty();
        if (this.readonly) {
            this.filterForm.disable();
        }
        this.filterForm.valueChanges
            .pipe(takeUntil(this._destroy$$))
            .subscribe(value1 => {
                this.parentForm.get([this.filterField]).patchValue(value1, {emitEvent: false});
                this.parentForm.get([this.filterField]).markAsDirty();
            });
        this.parentForm.get([this.filterField])
            .valueChanges
            .pipe(
                takeUntil(this._destroy$$)
            )
            .subscribe(value =>  {
                this.buildFilterControls(value);
                this.parentForm.get([this.filterField]).markAsDirty();
            });
    }

    private validateFilters = (_c: FormControl) => {
        let invalid = {};
        invalid[this.filterField] = {
            valid: false
        };
        return !!this.filterForm && this.filterForm.valid ? null : invalid;
    }

    private buildForm(item: FilterNodeObj): FormGroup {
        const group: { [id: string]: AbstractControl } = {};

        if (item.hasOwnProperty('type')) {
            if (item.type === 'empty') {
                group.type = new FormControl('group', Validators.required);
            } else {
                group.type = new FormControl(item.type, Validators.required);
            }
        }

        if (item.hasOwnProperty('groupType') || item.type === 'empty') {
            let groupNodeItem = item as GroupNodeObj;

            group.groupType = new FormControl(groupNodeItem.groupType, Validators.required);
            group.groupDifference = new FormControl(groupNodeItem.groupDifference);
            group.childs = !!groupNodeItem.childs ?
                this.formBuilder.array(groupNodeItem.childs.map(el => this.buildForm(el)), Validators.required) :
                this.formBuilder.array([], Validators.required);
        } else if (item.hasOwnProperty('ruleInfo')) {
            let ruleItem = item as RuleNodeObj;
            let ruleFormGroup: FormGroup = new FormGroup({
                variable: new FormControl(ruleItem.ruleInfo.variable, Validators.required),
                subVariable: new FormControl(ruleItem.ruleInfo.subVariable),
                difference: new FormControl(ruleItem.ruleInfo.difference),
                operator: new FormControl(ruleItem.ruleInfo.operator, Validators.required),
            }, {
                validators: [this.subVariableConditionallyRequiredValidator]
            });
            if (ruleItem.ruleInfo.operands && ruleItem.ruleInfo.operands.length > 0) {
                ruleFormGroup.addControl('operands', this.createOperandsFormArrayWithRule(ruleItem.ruleInfo));
            }

            group.ruleInfo = ruleFormGroup;
        }

        return this.formBuilder.group(group);
    }

    private subVariableConditionallyRequiredValidator = (formGroup: FormGroup) => {
        if (this.getVariableType(formGroup.value.variable).toUpperCase() === 'MAPSTRING') {
            return Validators.required(formGroup.get('subVariable')) ? {
                subVariable: true,
            } : null;
        }
        return null;
    }

    public getVariableType = (variableName: String): VariableType => {
        if (this.variables) {
            for (let trackerVar of this.variables) {
                if (trackerVar.name === variableName) {
                    return trackerVar.type;
                }
            }
        }

        return 'STRING';
    }

    private isStringType = (variableType: String) => {
        return variableType.toUpperCase() === 'STRING' || variableType.toUpperCase() === 'MAPSTRING';
    }

    private isComparableType = (variableType: String) => {
        return variableType.toUpperCase() === 'NUMBER' || variableType.toUpperCase() === 'DATE' ||
            variableType.toUpperCase() === 'TIME' || variableType.toUpperCase() === 'DATETIME';
    }

    protected getOperatorOptions = (variableValue: string) => {
        let variableType = this.getVariableType(variableValue);
        let operatorByType: Array<OperatorDetailsObj> = new Array<OperatorDetailsObj>();

        if (this.operatorDetails) {
            for (let opDetail of this.operatorDetails) {

                if (opDetail.basicOperator ||
                    (opDetail.stringOperator && this.isStringType(variableType)) ||
                    (opDetail.comparableOperator && this.isComparableType(variableType))) {
                    operatorByType.push(opDetail);
                }
            }
        }

        return operatorByType;
    }

    protected addRule = (form: FormArray) => {
        let newRule = new FormGroup({
            type: new FormControl('rule', Validators.required),
            ruleInfo: new FormGroup({
                variable: new FormControl('', Validators.required),
                subVariable: new FormControl(''),
                operator: new FormControl('', Validators.required),
                operands: new FormArray([], Validators.required),
                difference: new FormArray([])
            }, {
                validators: [this.subVariableConditionallyRequiredValidator]
            })
        });
        let childsArray: FormArray = form.get('childs') as FormArray;

        childsArray.push(newRule);
    }

    protected addGroup = (form: FormGroup) => {
        let newGroup = new FormGroup({
            type: new FormControl('group', Validators.required),
            groupType: new FormControl('and', Validators.required),
            childs: new FormArray([], Validators.required),
            groupDifference: new FormArray([])
        });
        let childsArray: FormArray = form.get('childs') as FormArray;

        childsArray.push(newGroup);
    }

    protected removeFormNode = (formArray: FormArray, controlIndex: number) => {
        formArray.removeAt(controlIndex);
    }

    private createOperandsFormArray (values: Array<string | number | Moment>, variableType?: VariableType): FormArray {
        let operands = new FormArray([]);
        for (let i = 0; i < values.length; i++) {
            let value = values[i];
            if (variableType && ['DATE', 'DATETIME', 'TIME'].includes(variableType)) {
                value = moment(value, this.utils.getDateFormat(variableType));
            }
            operands.push(new FormControl(value, Validators.required));
        }
        return operands;
    }

    private createOperandsFormArrayWithRule (ruleInfo: RuleReaderInfoObj): FormArray {
        let variableType = this.getVariableType(ruleInfo.variable);
        let operands = new FormArray([]);
        for (let i = 0; i < ruleInfo.operands.length; i++) {
            let operand: string | Moment = ruleInfo.operands[i];
            if (this.getVariableType(ruleInfo.variable) === 'LIST') {
                let validOperands = this.getDynamicOperands(ruleInfo.variable);
                if (!validOperands.some(validOperand => validOperand.key === operand)) {
                    this.notificationService.showErrorDialog('Warning',
                        ruleInfo.variable + ': the operand\'s value is no longer valid.',
                        'warn');
                    operand = null;
                }
            }
            if (variableType && ['DATE', 'DATETIME', 'TIME'].includes(variableType)) {
                operand = moment(operand, this.utils.getDateFormat(variableType));
            }
            operands.push(new FormControl(operand, Validators.required));
        }
        return operands;
    }

    protected variableChanged = (_matSelectChange: MatSelectChange, form: FormGroup) => {
        form.controls.subVariable.reset();
        form.controls.operator.reset();
        form.removeControl('operands');
    }

    savePreviousOperatorValue(event: any) {
        this.previousOperatorValue = event.value;
    }

    protected operatorChanged = (matSelectChange: MatSelectChange, form: FormGroup) => {
        let operatorData = this.operatorDetailsMap.get(matSelectChange.value);

        const isPreviousAnyOrNone = ['IS_ANY_OF', 'IS_NONE_OF'].includes(this.previousOperatorValue);
        const isPreviousBetween = ['IS_BETWEEN', 'IS_NOT_BETWEEN'].includes(this.previousOperatorValue);
        const isCurrentAnyOrNone = ['IS_ANY_OF', 'IS_NONE_OF'].includes(matSelectChange.value);
        const isCurrentBetween = ['IS_BETWEEN', 'IS_NOT_BETWEEN'].includes(matSelectChange.value);

        if (!isCurrentAnyOrNone && !isCurrentBetween) {
            form.removeControl('operands');
        } else if ((isPreviousAnyOrNone && isCurrentBetween) || (isPreviousBetween && isCurrentAnyOrNone)) {
            form.removeControl('operands');
        }

        if (operatorData && operatorData.operandsNumber !== -1) {
            form.addControl('operands', this.createOperandsFormArray(new Array(operatorData.operandsNumber)));
        } else if (this.isDynamicOperandsList((matSelectChange.value))) {
            form.addControl('operands', this.createOperandsFormArray([]));
        }
    }

    protected addOperand = (operands: FormArray) => {
        operands.push(new FormControl('', Validators.required));
    }

    protected removeOperand = (operands: FormArray, index: number) => {
        operands.removeAt(index);
    }

    protected updateOperand = (operand: FormControl, val: any) => {
        operand.setValue(val);
    }


    protected isDynamicOperandsList = (op: FilterOperator) => {

        if (op) {
            let operatorData = this.operatorDetailsMap.get(op);

            if (operatorData) {
                return operatorData.operandsNumber === -1;
            }
        }

        return false;
    }

    resetFilter(_form: FormGroup): void {
        this.buildFilterControls({type: 'empty'});
    }

    getDynamicOperands(variable: string): Array<KeyValueObj> {
        let elem = this.variables.find(item => item.name === variable);
        return this.dynamicOperandMap.get(elem.operandType);
    }

    onOpen(elem: HTMLDivElement): void {
        const matCalendar = document.getElementsByClassName('mat-datepicker-content')[0] as HTMLElement;
        matCalendar.appendChild(elem);
    }

    today(formControl: FormControl, datepicker: MatDatepicker<any>): void {
        formControl.setValue(moment());
        datepicker.close();
    }
}
